import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputAdornment,
  Checkbox,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DateRange } from "react-day-picker";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getFilteredBillsAction,
  getAllColorPinTagsAction,
  IRack,
} from "../../../redux/actions/pintagAction";
import { IColorPintag } from "../../../typings/interfaces/pos/pintag";
import { useSearchParams, useNavigate } from "react-router-dom";

import { PosLayout, CustomButton } from "../../../components";
import PintagTable from "./pintag-table";
import { Colors } from "../../../configs";
import { getAllRacksAction } from "../../../redux/actions/pintagAction";
import { DatePicker } from "../../../components/atoms/custom-date-time-picker/DateRangePicker";
import TopRightButton from "../../../components/common/top-right-button/top-right-button";
import { primaryAlpha } from "../../../configs/colors";

interface ISearchPintagScreenProps {
  openTab?: number;
}

const PAGE_SIZE = 15;

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const tabDefs = [
  { label: "Pintag Search" },
  { label: "Invoice Search" },
  { label: "General Search" },
];

export const SearchPintagScreen: React.FC<ISearchPintagScreenProps> = ({
  openTab = 0,
}) => {
  const [searchParams] = useSearchParams();
  const qpPin = searchParams.get("pintag");
  const qpInv = searchParams.get("invoice");
  const navigate = useNavigate();

  const [booted, setBooted] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState(openTab);
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined);
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const [rackLocation, setRackLocation] = React.useState<string | undefined>("All");
  const [sortBy, setSortBy] = React.useState<string>("None");
  const [generalQuery, setGeneralQuery] = React.useState<string>("");
  const [customerId] = React.useState<number | undefined>(undefined);
  const [pintagColors, setPintagColors] = React.useState<string[]>([]);
  const [pintagTag, setPintagTag] = React.useState<(string | number)[] | undefined>(
    () => (qpPin ? [qpPin] : qpInv ? [qpInv] : undefined)
  );
  const [invoiceTag, setInvoiceTag] = React.useState<(string | number)[] | undefined>(
    () => (qpInv ? [qpInv] : undefined)
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const { getRacks, getColorPintags } = useSelector((state: RootState) => state.pintag);
  const {
    filteredBills,
    filteredBillsLoading,
    allLoadedBills,
    currentPage,
    hasMoreData,
    loadingNextPage,
  } = useSelector((state: RootState) => state.pintag);

  const debouncedPintagTag = useDebounce(pintagTag?.join(",") || "", 500);
  const debouncedInvoiceTag = useDebounce(invoiceTag?.join(",") || "", 500);
  const debouncedGeneralQuery = useDebounce(generalQuery, 500);

  useEffect(() => {
    dispatch(getAllRacksAction());
    dispatch(getAllColorPinTagsAction());
  }, [dispatch]);

  useEffect(() => {
    const pin = searchParams.get("pintag");
    const inv = searchParams.get("invoice");
    const tabParam = searchParams.get("tab");
    if (pin) setPintagTag([pin]);
    if (inv) setInvoiceTag([inv]);
    if (tabParam !== null) setSelectedTab(Number(tabParam));
    setBooted(true);
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const tabIndex = tabParam !== null ? Number(tabParam) : openTab;
    if (!Number.isNaN(tabIndex) && tabIndex !== selectedTab) setSelectedTab(tabIndex);
    const pin = searchParams.get("pintag");
    const inv = searchParams.get("invoice");
    if (pin && (pintagTag?.length !== 1 || pintagTag?.[0] !== pin)) setPintagTag([pin]);
    if (inv && (invoiceTag?.length !== 1 || invoiceTag?.[0] !== inv)) setInvoiceTag([inv]);
  }, [searchParams]);

  useEffect(() => {
    if (!booted) return;
    const pin = searchParams.get("pintag");
    const inv = searchParams.get("invoice");
    const tabParam = searchParams.get("tab");
    if (pin) setPintagTag([pin]);
    else setPintagTag(undefined);
    if (inv) setInvoiceTag([inv]);
    else setInvoiceTag(undefined);
    if (tabParam !== null) setSelectedTab(Number(tabParam));
  }, [searchParams, booted]);

  const requestPayload = React.useMemo(() => {
    if (!booted) return null;
    const base: any = {
      customerId,
      dueDateFrom: dateRange?.from ? dateRange.from.toISOString() : undefined,
      rackLocation: rackLocation !== "All" ? rackLocation : undefined,
      page: 0,
      size: PAGE_SIZE,
    };
    if (selectedTab === 0 && debouncedPintagTag) {
      base.pintagTag = debouncedPintagTag.split(",").map((t) => t.trim()).filter(Boolean);
    }
    if (selectedTab === 1 && debouncedInvoiceTag) {
      base.invoice = debouncedInvoiceTag.split(",").map((t) => t.trim()).filter(Boolean);
    }
    if (selectedTab === 2 && debouncedGeneralQuery) {
      base.generalQuery = debouncedGeneralQuery;
    }
    return base;
  }, [
    booted, selectedTab, debouncedPintagTag, debouncedInvoiceTag,
    debouncedGeneralQuery, customerId, dateRange, rackLocation,
  ]);

  const lastPayloadRef = React.useRef<string>("");
  useEffect(() => {
    if (!requestPayload) return;
    const key = JSON.stringify(requestPayload);
    if (key !== lastPayloadRef.current) {
      lastPayloadRef.current = key;
      dispatch(getFilteredBillsAction(requestPayload as any));
    }
  }, [requestPayload, dispatch]);

  const loadNextPage = useCallback(async () => {
    if (!requestPayload || loadingNextPage || !hasMoreData || filteredBillsLoading) return;
    dispatch(getFilteredBillsAction({ ...requestPayload, page: currentPage + 1, size: PAGE_SIZE }));
  }, [requestPayload, loadingNextPage, hasMoreData, filteredBillsLoading, currentPage, dispatch]);

  const handleScroll = useCallback(() => {
    if (tableRef.current && !loadingNextPage && hasMoreData && !filteredBillsLoading) {
      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100) loadNextPage();
    }
  }, [loadingNextPage, hasMoreData, filteredBillsLoading, loadNextPage]);

  const handleDateChange = (val: Date | DateRange | undefined) => {
    if (!val) {
      setDateRange(undefined);
      setDatePickerOpen(false);
    } else {
      setDateRange(val as DateRange);
      if ((val as DateRange).to) setDatePickerOpen(false);
    }
  };

  const visibleBills = React.useMemo(() => {
    let bills = [...(allLoadedBills?.length > 0 ? allLoadedBills : (filteredBills?.body?.content ?? []))];

    if (dateRange?.from || dateRange?.to) {
      bills = bills.filter((b) => {
        if (!b.createdTime) return false;
        const t = new Date(b.createdTime).getTime();
        const from = dateRange?.from
          ? new Date(new Date(dateRange.from).setHours(0, 0, 0, 0)).getTime()
          : -Infinity;
        const to = dateRange?.to
          ? new Date(new Date(dateRange.to).setHours(23, 59, 59, 999)).getTime()
          : Infinity;
        return t >= from && t <= to;
      });
    }

    if (pintagColors.length > 0) {
      bills = bills.filter((b) => b.pintagColor && pintagColors.includes(b.pintagColor));
    }

    const sanitize = (s: string) =>
      (s ?? "").toString().toLowerCase().replace(/[\s_-]+/g, " ").trim();

    if (selectedTab === 1 && invoiceTag?.length) {
      const needles = invoiceTag.join(",").split(",").map(sanitize).filter(Boolean);
      if (needles.length) {
        bills = bills.filter((b) => {
          const candidates = [b?.billId, b?.invoiceNo, b?.invoice, b?.invoiceNumber].map(sanitize);
          return needles.some((n) => candidates.some((c) => c.includes(n)));
        });
      }
    }

    if (selectedTab === 2 && generalQuery.trim()) {
      const tokens = sanitize(generalQuery).split(" ").filter(Boolean);
      bills = bills.filter((b) => {
        const hay = sanitize(
          [
            b?.billId, b?.invoiceNo, b?.invoice, b?.invoiceNumber,
            b?.pintagTag, b?.pintagTags, b?.pintagColor, b?.pintagColorDescription,
            b?.customerId, b?.customerName, b?.rack, b?.rackLocation,
            b?.pickupCashier, b?.dropCashier, b?.description,
            b?.dateIn, b?.createdTime, b?.dueDate, b?.pickupDate, b?.pieces,
          ].filter(Boolean).join(" ")
        );
        return tokens.every((t) => hay.includes(t));
      });
    }

    if (sortBy === "latestInvoice") {
      return bills.sort((a, b) =>
        new Date(b.lastPaymentDate || "").getTime() - new Date(a.lastPaymentDate || "").getTime()
      );
    }
    if (sortBy === "latestPintag") {
      return bills.sort((a, b) =>
        new Date(b.createdTime || "").getTime() - new Date(a.createdTime || "").getTime()
      );
    }

    return bills;
  }, [filteredBills, selectedTab, invoiceTag, generalQuery, sortBy, allLoadedBills, dateRange, pintagColors]);

  const searchInputValue =
    selectedTab === 0
      ? pintagTag?.join(",") || ""
      : selectedTab === 1
      ? invoiceTag?.join(",") || ""
      : generalQuery;

  const searchInputPlaceholder =
    selectedTab === 0
      ? "Search by pintag number"
      : selectedTab === 1
      ? "Search by invoice number"
      : "Search by name, invoice, pintag…";

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (selectedTab === 0) setPintagTag(val.split(",").map((t) => t.trim()));
    else if (selectedTab === 1) setInvoiceTag(val.split(",").map((t) => t.trim()));
    else setGeneralQuery(val);
  };

  const handleReset = () => {
    setDateRange(undefined);
    setRackLocation("All");
    setSortBy("None");
    setGeneralQuery("");
    setPintagTag(undefined);
    setInvoiceTag(undefined);
    setPintagColors([]);
  };

  const inputSx = {
    backgroundColor: Colors.WHITE,
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.75rem",
      fontSize: "1rem",
      "& fieldset": { border: "1.5px solid #E5E7EB" },
      "&:hover fieldset": { borderColor: Colors.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: Colors.SECONDARY },
    },
    "& input::placeholder": { fontSize: "0.95rem", opacity: 0.6 },
  };

  const selectSx = {
    height: "2.75rem",
    fontSize: "0.95rem",
    borderRadius: "0.75rem",
    backgroundColor: Colors.WHITE,
    "& .MuiOutlinedInput-notchedOutline": { border: "1.5px solid #E5E7EB" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.SECONDARY },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: Colors.SECONDARY },
  };

  const labelSx = {
    fontSize: "0.82rem",
    fontWeight: 700,
    color: Colors.TEXT_GRAY_DARK,
    mb: "0.4rem",
  };

  const colorPintags: IColorPintag[] = (getColorPintags as any)?.body ?? [];

  return (
    <PosLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          px: "2.5rem",
          pt: "1.5rem",
          pb: "1rem",
          gap: "1.25rem",
        }}
      >
        {/* ── Header row ── */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                color: Colors.SECONDARY,
                lineHeight: 1.3,
              }}
            >
              Search System
            </Typography>
            <Typography
              sx={{
                fontSize: "2rem",
                fontWeight: 700,
                color: Colors.TEXT_GRAY_DARK,
                lineHeight: "2.4rem",
                letterSpacing: "-0.02em",
              }}
            >
              {tabDefs[selectedTab]?.label}
            </Typography>
          </Box>
          <TopRightButton variation="Back" onClick={() => navigate("/pos/search-general")} />
        </Box>

        {/* ── Tab selector ── */}
        <Box sx={{ display: "flex", gap: "0.65rem" }}>
          {tabDefs.map((tab, i) => (
            <CustomButton
              key={i}
              onClick={() => setSelectedTab(i)}
              sx={{
                height: "2.75rem",
                px: "1.5rem",
                borderRadius: "0.65rem",
                fontSize: "0.95rem",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: selectedTab === i ? `0 4px 14px ${primaryAlpha(0.25)}` : "none",
                backgroundColor: selectedTab === i ? Colors.PRIMARY : Colors.BACKGROUND_GRAY,
                color: selectedTab === i ? Colors.WHITE : Colors.TEXT_GRAY_DARK,
                border: selectedTab === i ? "none" : `1.5px solid #E5E7EB`,
                "&:hover": {
                  backgroundColor: selectedTab === i ? Colors.PRIMARY_DARK : "#EFEFEF",
                  boxShadow: selectedTab === i ? `0 6px 18px ${primaryAlpha(0.30)}` : "none",
                },
              }}
            >
              {tab.label}
            </CustomButton>
          ))}
        </Box>

        {/* ── Content card ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: Colors.WHITE,
            borderRadius: "1.2rem",
            boxShadow: "0 8px 28px rgba(0,0,0,0.07)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Filter bar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              gap: "1.25rem",
              px: "1.75rem",
              pt: "1.5rem",
              pb: "1.25rem",
              borderBottom: "1.5px solid #F0F0F0",
              flexWrap: "wrap",
            }}
          >
            {/* Date Range */}
            <Box sx={{ minWidth: "16rem" }}>
              <Typography sx={labelSx}>Date Range (Date In)</Typography>
              <DatePicker
                type="range"
                value={dateRange}
                onChange={handleDateChange}
                open={datePickerOpen}
                onOpenChange={setDatePickerOpen}
                className="bg-white h-[2.75rem] text-[0.95rem] rounded-xl border border-[#E5E7EB]"
              />
            </Box>

            {/* Color Code — multi-select */}
            {colorPintags.length > 0 && (
              <Box sx={{ minWidth: "13rem" }}>
                <Typography sx={labelSx}>Color Code</Typography>
                <Select
                  multiple
                  displayEmpty
                  value={pintagColors}
                  onChange={(e) => setPintagColors(e.target.value as string[])}
                  renderValue={(selected) => {
                    const sel = selected as string[];
                    if (sel.length === 0) {
                      return (
                        <span style={{ color: Colors.ACCENT_GRAY_DISABLED, fontSize: "0.9rem" }}>
                          All Colors
                        </span>
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                        {sel.map((color) => (
                          <Box
                            key={color}
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "0.25rem",
                              backgroundColor: color,
                              border: "1px solid rgba(0,0,0,0.12)",
                              flexShrink: 0,
                            }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                  sx={selectSx}
                >
                  {colorPintags.map((c: IColorPintag) => (
                    <MenuItem key={c.id} value={c.color} sx={{ gap: "0.5rem" }}>
                      <Checkbox
                        checked={pintagColors.includes(c.color)}
                        size="small"
                        sx={{ p: 0, mr: "0.4rem", color: Colors.SECONDARY, "&.Mui-checked": { color: Colors.SECONDARY } }}
                      />
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "0.3rem",
                          backgroundColor: c.color,
                          border: "1px solid rgba(0,0,0,0.1)",
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={c.description || c.alias || c.color}
                        primaryTypographyProps={{ fontSize: "0.9rem" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {/* Rack Location */}
            <Box sx={{ minWidth: "13rem" }}>
              <Typography sx={labelSx}>Rack Location</Typography>
              <Select
                fullWidth
                size="small"
                value={rackLocation || "All"}
                onChange={(e) => setRackLocation(e.target.value || "All")}
                sx={selectSx}
              >
                <MenuItem value="All" sx={{ fontSize: "0.95rem" }}>All Racks</MenuItem>
                {getRacks?.body?.map((rack: IRack) => (
                  <MenuItem key={rack.id} value={rack.rackLocation} sx={{ fontSize: "0.95rem" }}>
                    Rack {rack.rackLocation}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Sort By */}
            <Box sx={{ minWidth: "13rem" }}>
              <Typography sx={labelSx}>Sort By</Typography>
              <Select
                fullWidth
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="None" sx={{ fontSize: "0.95rem" }}>None</MenuItem>
                <MenuItem value="latestInvoice" sx={{ fontSize: "0.95rem" }}>Latest Invoice</MenuItem>
                <MenuItem value="latestPintag" sx={{ fontSize: "0.95rem" }}>Latest Pintag</MenuItem>
              </Select>
            </Box>

            {/* Search input */}
            <Box sx={{ flex: 1, minWidth: "14rem" }}>
              <Typography sx={labelSx}>Search</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                placeholder={searchInputPlaceholder}
                value={searchInputValue}
                onChange={handleSearchInputChange}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ fontSize: "1.3rem", color: Colors.ACCENT_GRAY_DISABLED }} />
                    </InputAdornment>
                  ),
                  sx: { height: "2.75rem" },
                }}
                sx={inputSx}
              />
            </Box>

            {/* Reset */}
            <Box sx={{ alignSelf: "flex-end" }}>
              <CustomButton
                onClick={handleReset}
                sx={{
                  height: "2.75rem",
                  px: "1.25rem",
                  borderRadius: "0.75rem",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: Colors.BACKGROUND_GRAY,
                  color: Colors.TEXT_GRAY_DARK,
                  border: "1.5px solid #E5E7EB",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#EFEFEF" },
                }}
              >
                Reset
              </CustomButton>
            </Box>
          </Box>

          {/* Table area */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              px: "1.75rem",
              pt: "1rem",
              pb: "1rem",
            }}
          >
            <PintagTable
              ref={tableRef}
              data={visibleBills}
              isLoading={filteredBillsLoading && allLoadedBills.length === 0}
              onScroll={handleScroll}
              loadingNextPage={loadingNextPage}
              hasMoreData={hasMoreData}
            />
          </Box>
        </Box>
      </Box>
    </PosLayout>
  );
};

export default SearchPintagScreen;
